import { notEqual } from '@/api/helpers';
import { Branch, DeleteBranchSettings } from '@/client/branches';
import { LoadingStatuses } from '@/common/constants';
import { BranchesSelectInput } from '@/components/form/selectors/BranchesSelectInput';
import { AppButton } from '@/ui/buttons';
import { AppCheckbox } from '@/ui/checkbox/AppCheckbox';
import { CheckboxChangeEvent } from 'primereact/checkbox';
import { Dialog } from 'primereact/dialog';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const StyledDialog = styled(Dialog)`
  width: 550px;
  max-width: 90vw;

  .p-dialog-content {
    padding: var(--default-padding) var(--big-padding);
  }

  .p-dialog-footer {
    padding: var(--big-padding);
    padding-top: 0;
    border-top: none;
  }

  .small-text {
    font-size: var(--xsmall-font-size);
  }
`;

const StyledWarningBox = styled.div`
  background: var(--beige-main);
  border-radius: var(--xsmall-border-radius);
  margin: var(--default-padding) 0;
`;

const StyledWarningTitle = styled.h3`
  font-size: var(--small-font-size);
  font-weight: 700;
  color: var(--black-main);
  margin: 0 0 var(--small-padding) 0;
`;

const StyledWarningList = styled.ul`
  margin: 0;
  padding: 20px;

  li {
    font-size: var(--small-font-size);
    line-height: var(--small-line-height);
    color: var(--black-main);
    margin-bottom: var(--xsmall-padding);
    list-style-type: none;
    padding-left: var(--small-padding);
    position: relative;

    &::before {
      content: '•';
      display: inline-block;
      width: 1em;
      position: absolute;
      left: calc(var(--xxsmall-padding) * -1);
    }

    &::marker {
      display: none;
    }

    &:last-child {
      margin-bottom: 0;
    }

    strong {
      font-weight: 600;
    }
  }
`;

const StyledWrapper = styled.div`
  margin-bottom: var(--default-padding);
`;

const StyledBranchesSelectInput = styled(BranchesSelectInput)`
  width: 100%;

  .p-dropdown {
    background: var(--gray-pale);
    border: 1px solid var(--gray-dark);
    border-radius: var(--xsmall-border-radius);

    .p-dropdown-label {
      color: var(--gray-darker);
      font-size: var(--small-font-size);
    }
  }
`;

const StyledFooter = styled.div`
  display: flex;
  align-items: center;
  margin-top: var(--big-padding);
  gap: var(--xxsmall-padding);
`;

type DeleteBranchModalProps = {
  visible: boolean;
  branchName: string;
  branchId: string;
  isDeleting?: boolean;
  onHide: () => void;
  onDelete: (settings: DeleteBranchSettings) => void;
};

export const DeleteBranchModal: React.FC<DeleteBranchModalProps> = ({
  visible,
  branchName,
  branchId,
  isDeleting,
  onHide,
  onDelete,
}) => {
  const { t } = useTranslation();
  const [shouldDeleteUsers, setShouldDeleteUsers] = useState(false);
  const [reassignUsers, setReassignUsers] = useState(false);
  const [reAssignChildBranches, setReAssignChildBranches] = useState(false);
  const [targetBranchForUsers, setTargetBranchForUsers] = useState<
    Branch | undefined
  >();
  const [reAssignChildBranchesTo, setReAssignChildBranchesTo] = useState<
    Branch | undefined
  >();

  const handleDelete = () => {
    const settings: DeleteBranchSettings = {
      shouldDeleteUsers,
      reassignUsers,
      reAssignChildBranches,
      reAssignUsersToBranch: reassignUsers
        ? targetBranchForUsers?.id
        : undefined,
      reAssignChildBranchesTo: reAssignChildBranches
        ? reAssignChildBranchesTo?.id
        : undefined,
    };
    onDelete(settings);
  };

  const getUsersWarningText = () => {
    if (shouldDeleteUsers) {
      return t('branch.delete.settings.delete.users');
    }

    if (reassignUsers) {
      return t('branch.delete.settings.reassign.users');
    }

    return t('branch.delete.settings.unassign.users');
  };

  const getSubBranchesWarningText = () => {
    if (reAssignChildBranches) {
      return t('branch.delete.settings.reassign.subbranches');
    }

    return t('branch.delete.settings.unassign.subbranches');
  };

  return (
    <StyledDialog
      visible={visible}
      draggable={false}
      header={t('branch.delete')}
      closable
      className="v2"
      modal
      onHide={onHide}
    >
      <div>
        <p
          style={{
            fontSize: 'var(--small-font-size)',
            marginBottom: 'var(--default-padding)',
          }}
        >
          {t('branch.delete.you.are.about.to.delete', {
            branchName,
          })}
        </p>

        <StyledWarningTitle>
          {t('branch.delete.what.will.happen')}
        </StyledWarningTitle>

        <StyledWarningBox>
          <StyledWarningList>
            <li
              dangerouslySetInnerHTML={{
                __html: t('branch.delete.settings.warning'),
              }}
            />
            <li dangerouslySetInnerHTML={{ __html: getUsersWarningText() }} />
            <li
              dangerouslySetInnerHTML={{
                __html: getSubBranchesWarningText(),
              }}
            />
            <li>{t('branch.delete.settings.action.cannot.be.undone')}</li>
          </StyledWarningList>
        </StyledWarningBox>

        <p className="small-text">
          {t('branch.delete.settings')}
          <span className="red">*</span>
        </p>

        <StyledWrapper>
          <AppCheckbox
            key="delete-users"
            label={t('branch.delete.settings.delete.users.checkbox')}
            checked={shouldDeleteUsers}
            disabled={reassignUsers || isDeleting}
            onChange={(e: CheckboxChangeEvent) =>
              setShouldDeleteUsers(e.checked ?? false)
            }
          />
        </StyledWrapper>

        <StyledWrapper>
          <AppCheckbox
            key="reassign-users"
            label={t('branch.delete.settings.reassign.users.checkbox')}
            checked={reassignUsers}
            disabled={shouldDeleteUsers || isDeleting}
            onChange={(e: CheckboxChangeEvent) =>
              setReassignUsers(e.checked ?? false)
            }
          />
          {reassignUsers && (
            <StyledBranchesSelectInput
              placeholder={t('branch.choose.branch')}
              selectedValue={targetBranchForUsers}
              isDisabled={isDeleting}
              onChange={setTargetBranchForUsers}
              additionalFilters={[notEqual('id', branchId)]}
            />
          )}
        </StyledWrapper>

        <StyledWrapper>
          <AppCheckbox
            key="reassign-sub-branches"
            label={t('branch.delete.settings.reassign.subbranches.checkbox')}
            checked={reAssignChildBranches}
            disabled={isDeleting}
            onChange={(e: CheckboxChangeEvent) =>
              setReAssignChildBranches(e.checked ?? false)
            }
          />
          {reAssignChildBranches && (
            <StyledBranchesSelectInput
              placeholder={t('branch.choose.new.parent.branch')}
              selectedValue={reAssignChildBranchesTo}
              isDisabled={isDeleting}
              onChange={setReAssignChildBranchesTo}
              additionalFilters={[notEqual('id', branchId)]}
            />
          )}
        </StyledWrapper>
      </div>
      <StyledFooter>
        <AppButton
          label={t('button.cancel')}
          severity="danger"
          isDisabled={isDeleting}
          onClick={onHide}
        />
        <AppButton
          type="text"
          severity="secondary"
          isDisabled={
            (reassignUsers && !targetBranchForUsers) ||
            (reAssignChildBranches && !reAssignChildBranchesTo)
          }
          state={isDeleting ? LoadingStatuses.LOADING : LoadingStatuses.IDLE}
          icon="pi pi-trash"
          label={t('branch.delete.button')}
          onClick={handleDelete}
        />
      </StyledFooter>
    </StyledDialog>
  );
};
